import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { fontFamily } from "../../shared/styles/theme";
import { Link } from "react-router-dom";
import { Icon } from "../../shared/icons/generate-icon";
import {
  desktopDevice,
  deviceMargin,
  largeScreens,
  tabletDevice,
} from "../../shared/styles/sizes";
import { FaLinkedinIn, FaFacebookF, FaInstagram } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { SiTiktok } from "react-icons/si";
import { FiSend } from "react-icons/fi";
import { primaryColor } from "../../shared/styles/color";
import { getHomeDashboardPath } from "../../../utils/dashboardRoutes";
import { subscribeNewsletterService } from "../../../services/newsletter/newsletter-service";

const TEAL = primaryColor;
const FOOTER_BG = "#f5f7f8";
const LINK_COLOR = "#555555";
const LIGHT_BLUE_BORDER = "#b8d4e8";
const MOBILE_BREAK = 768;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "null");
  } catch {
    return null;
  }
}

function validateNewsletterEmail(value) {
  const next = String(value || "").trim();
  if (!next) return "Please enter your email address.";
  if (next.length > 255 || !EMAIL_RE.test(next)) {
    return "Please enter a valid email address.";
  }
  return "";
}

const FormMessage = styled.p`
  font-family: ${fontFamily.font};
  font-size: 13px;
  line-height: 1.4;
  margin: 0;
  color: ${(p) => (p.$tone === "success" ? "#15803d" : "#dc2626")};
`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [sessionTick, setSessionTick] = useState(0);
  const [subscribing, setSubscribing] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  useEffect(() => {
    const onUp = () => setSessionTick((n) => n + 1);
    window.addEventListener("liber-user-updated", onUp);
    window.addEventListener("storage", onUp);
    return () => {
      window.removeEventListener("liber-user-updated", onUp);
      window.removeEventListener("storage", onUp);
    };
  }, []);

  const { isLoggedIn, isFreelancer, isBusiness, dashboardPath } = useMemo(() => {
    void sessionTick;
    const userData = readUserData();
    const user = userData?.user;
    const userType = user?.user_type;
    const freelancer =
      userType === "freelancers" || userType === "freelancer";
    const business = userType === "business";
    return {
      isLoggedIn: !!(userData?.token && user),
      isFreelancer: freelancer,
      isBusiness: business,
      dashboardPath: getHomeDashboardPath(user),
    };
  }, [sessionTick]);

  const showPostProject = !isLoggedIn || isBusiness;
  const showLogin = !isLoggedIn;
  const showCreateProfile = !isLoggedIn;

  const businessLinks = useMemo(() => {
    const links = [
      { label: "Find Talent", to: "/browse-freelancers" },
    ];
    if (showPostProject) {
      links.push({
        label: "Post a Project",
        to: isLoggedIn ? "/add-project" : "/login",
      });
    }
    links.push(
      { label: "How It Works", to: "/how-it-works" },
      { label: "Pricing", to: "/pricing" },
    );
    if (isBusiness) {
      links.push({ label: "Dashboard", to: dashboardPath });
    }
    return links;
  }, [showPostProject, isLoggedIn, isBusiness, dashboardPath]);

  const freelancerLinks = useMemo(() => {
    const links = [{ label: "Find Work", to: "/browse-projects" }];
    if (showCreateProfile) {
      links.push({ label: "Create Profile", to: "/sign-up" });
    }
    if (showLogin) {
      links.push({ label: "Login", to: "/login" });
    }
    if (isFreelancer) {
      links.push(
        { label: "My Profile", to: "/profile" },
        { label: "Get Verified", to: "/vetting" },
        { label: "Dashboard", to: dashboardPath },
      );
    }
    links.push(
      { label: "How It Works", to: "/how-it-works" },
      { label: "Success Stories", to: "/about-us" },
    );
    return links;
  }, [showCreateProfile, showLogin, isFreelancer, dashboardPath]);

  const resourceLinks = useMemo(
    () => [
      { label: "Blog", to: "/blog" },
      { label: "Help Center", to: "/help-center" },
      { label: "Terms & Conditions", to: "/terms-and-conditions" },
      { label: "Cookie Policy", to: "/cookie-policy" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Disclaimer", to: "/disclaimer" },
    ],
    [],
  );

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setFormSuccess("");
    const validationError = validateNewsletterEmail(email);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormError("");
    setSubscribing(true);
    try {
      const response = await subscribeNewsletterService({
        email: email.trim(),
        source: "footer",
      });
      const status = response?.status;
      const message =
        response?.data?.message ||
        (status >= 200 && status < 300
          ? "Thanks for subscribing!"
          : "Could not subscribe. Please try again.");

      if (status >= 200 && status < 300) {
        setFormSuccess(message);
        setFormError("");
        setEmail("");
      } else {
        setFormSuccess("");
        setFormError(message);
      }
    } catch {
      setFormSuccess("");
      setFormError("Could not subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <FooterContainer>
      <TopSection>
        <BrandColumn>
          <LogoLink to="/home">
            <LogoWrap>
              <LogoIcon>
                <Icon src={"/Logo.png"} style={{ height: "38px", width: "38px" }} />
              </LogoIcon>
              <BrandName>Liber</BrandName>
            </LogoWrap>
          </LogoLink>
          <IntroText>
            We connect businesses with trusted talent, making it easier to get the right projects done.
          </IntroText>
          {showPostProject && (
            <PostProjectLink to={isLoggedIn ? "/add-project" : "/login"}>
              Post a Project
            </PostProjectLink>
          )}
        </BrandColumn>

        <LinksColumn>
          <ColumnHeading>For Businesses</ColumnHeading>
          <LinkList>
            {businessLinks.map((link) => (
              <LinkItem key={`${link.label}-${link.to}`}>
                <FooterNavLink to={link.to}>{link.label}</FooterNavLink>
              </LinkItem>
            ))}
          </LinkList>
        </LinksColumn>

        <LinksColumn>
          <ColumnHeading>For Freelancers</ColumnHeading>
          <LinkList>
            {freelancerLinks.map((link) => (
              <LinkItem key={`${link.label}-${link.to}`}>
                <FooterNavLink to={link.to}>{link.label}</FooterNavLink>
              </LinkItem>
            ))}
          </LinkList>
        </LinksColumn>

        <LinksColumn>
          <ColumnHeading>Resources</ColumnHeading>
          <LinkList>
            {resourceLinks.map((link) => (
              <LinkItem key={link.to}>
                <FooterNavLink to={link.to}>{link.label}</FooterNavLink>
              </LinkItem>
            ))}
          </LinkList>
        </LinksColumn>

        <NewsletterColumn>
          <ColumnHeading>Newsletter</ColumnHeading>
          <NewsletterText>
            Stay in the loop with job updates, business tips, and freelance trends
            across MENA.
          </NewsletterText>
          <NewsletterForm onSubmit={handleSubscribe} noValidate>
            <EmailInput
              type="email"
              name="newsletter_email"
              autoComplete="email"
              placeholder="Enter email address"
              value={email}
              disabled={subscribing}
              $invalid={!!formError}
              aria-invalid={!!formError}
              aria-describedby={
                formError
                  ? "newsletter-error"
                  : formSuccess
                    ? "newsletter-success"
                    : undefined
              }
              onChange={(e) => {
                setEmail(e.target.value);
                if (formError) setFormError("");
                if (formSuccess) setFormSuccess("");
              }}
            />
            {formError ? (
              <FormMessage id="newsletter-error" $tone="error" role="alert">
                {formError}
              </FormMessage>
            ) : null}
            {formSuccess ? (
              <FormMessage id="newsletter-success" $tone="success" role="status">
                {formSuccess}
              </FormMessage>
            ) : null}
            <SubscribeButton type="submit" disabled={subscribing}>
              {subscribing ? "Subscribing…" : "Subscribe"}
              {!subscribing ? (
                <PaperPlaneIcon as="span">
                  <FiSend size={18} color="#ffffff" />
                </PaperPlaneIcon>
              ) : null}
            </SubscribeButton>
          </NewsletterForm>
        </NewsletterColumn>
      </TopSection>

      <BottomRow>
        <Copyright>© {new Date().getFullYear()} Liber. All rights reserved.</Copyright>
        <SocialRow>
          <SocialLink href="https://tiktok.com" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
            <SiTiktok size={18} />
          </SocialLink>
          <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <FaInstagram size={18} />
          </SocialLink>
          <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <FaLinkedinIn size={18} />
          </SocialLink>
          <SocialLink href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <FaFacebookF size={18} />
          </SocialLink>
          <SocialLink href="https://x.com" target="_blank" rel="noopener noreferrer" aria-label="X">
            <FaXTwitter size={18} />
          </SocialLink>
        </SocialRow>
      </BottomRow>
    </FooterContainer>
  );
};

const FooterNavLink = styled(Link)`
  text-decoration: none;
  color: ${LINK_COLOR};
  font-family: ${fontFamily.font};
  font-size: 15px;
  display: inline-block;
  text-align: left;
  transition: color 0.2s;

  &:hover {
    color: ${TEAL};
  }
`;

const FooterContainer = styled.footer`
  width: 100%;
  background-color: ${FOOTER_BG};
  padding: 48px ${deviceMargin.mobile}px 24px;
  box-sizing: border-box;

  @media (min-width: ${tabletDevice}px) {
    padding: 56px ${deviceMargin.tablet}px 28px;
  }

  @media (min-width: ${desktopDevice}px) {
    padding: 64px ${deviceMargin.largeScreen}px 32px;
  }

  @media (min-width: ${largeScreens}px) {
    padding: 64px ${deviceMargin.largeScreen}px 32px;
  }
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  box-sizing: border-box;
  text-align: left;

  @media (min-width: ${tabletDevice}px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
  }

  @media (min-width: ${desktopDevice}px) {
    grid-template-columns: 1.4fr 1fr 1fr 1fr 1.2fr;
    gap: 28px;
  }
`;

const BrandColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 14px;
  text-decoration: none;
`;

const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LogoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const BrandName = styled.span`
  font-family: ${fontFamily.font};
  font-weight: 700;
  font-size: 22px;
  color: ${TEAL};
`;

const IntroText = styled.p`
  font-family: ${fontFamily.font};
  font-size: 15px;
  line-height: 1.55;
  color: ${LINK_COLOR};
  margin: 0 0 20px;
  max-width: 100%;
  text-align: left;

  @media (min-width: ${desktopDevice}px) {
    max-width: 280px;
  }
`;

const PostProjectLink = styled(Link)`
  font-family: ${fontFamily.font};
  font-weight: 600;
  border-radius: 999px;
  color: ${TEAL};
  font-size: 15px;
  background: #ffffff;
  border: 1.5px solid ${LIGHT_BLUE_BORDER};
  padding: 11px 22px;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    border-color: ${TEAL};
    background: #e8f4f8;
  }
`;

const LinksColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  text-align: left;
`;

const ColumnHeading = styled.h3`
  font-family: ${fontFamily.font};
  font-weight: 700;
  font-size: 16px;
  color: #1d1e2b;
  margin: 0 0 12px;
  text-align: left;
  width: 100%;

  @media (min-width: ${tabletDevice}px) {
    font-size: 17px;
    margin: 0 0 16px;
  }
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const LinkItem = styled.li`
  margin-bottom: 8px;
  line-height: 1.5;
  text-align: left;
  width: 100%;

  &:last-child {
    margin-bottom: 0;
  }

  @media (min-width: ${tabletDevice}px) {
    margin-bottom: 10px;
  }
`;

const NewsletterColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  text-align: left;

  @media (max-width: ${MOBILE_BREAK - 1}px) {
    border-top: 1px solid #e8e8e8;
    padding-top: 24px;
    margin-top: 4px;
  }
`;

const NewsletterText = styled.p`
  font-family: ${fontFamily.font};
  font-size: 15px;
  line-height: 1.5;
  color: ${LINK_COLOR};
  margin: 0 0 16px;
  text-align: left;
  width: 100%;
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const EmailInput = styled.input`
  font-family: ${fontFamily.font};
  font-size: 14px;
  padding: 12px 14px;
  border: 1px solid ${(p) => (p.$invalid ? "#ef4444" : "#ddd")};
  border-radius: 8px;
  background: #ffffff;
  color: #333;
  width: 100%;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    border-color: ${(p) => (p.$invalid ? "#ef4444" : TEAL)};
    box-shadow: 0 0 0 3px
      ${(p) =>
        p.$invalid ? "rgba(239, 68, 68, 0.15)" : "rgba(17, 138, 178, 0.15)"};
  }
`;

const SubscribeButton = styled.button`
  font-family: ${fontFamily.font};
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  background: ${TEAL};
  border: none;
  border-radius: 8px;
  padding: 12px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PaperPlaneIcon = styled.span`
  display: inline-flex;
  align-items: center;
`;

const BottomRow = styled.div`
  max-width: 1280px;
  margin: 28px auto 0;
  padding-top: 20px;
  border-top: 1px solid #e0e4e8;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  text-align: left;
  box-sizing: border-box;

  @media (min-width: ${tabletDevice}px) {
    margin: 32px auto 0;
    padding-top: 24px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
`;

const Copyright = styled.span`
  font-family: ${fontFamily.font};
  font-size: 13px;
  color: ${LINK_COLOR};
  text-align: left;
`;

const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SocialLink = styled.a`
  color: #1d1e2b;
  transition: color 0.2s;

  &:hover {
    color: ${TEAL};
  }
`;
